import { Injectable, inject, signal, DestroyRef } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { defer, from, Observable, of, throwError, Subject, ReplaySubject, race } from "rxjs";
import {
  catchError,
  finalize,
  map,
  share,
  tap,
  switchMap,
  take,
} from "rxjs/operators";
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MfaApiService as GeneratedMfaService } from "@api/api/mfa.service";
import { MfaStatusResponse } from "@api/model/models";
import { environment } from "../../../environments/environment";
import { LoggerService } from "./logger.service";
import { DynamicDialogService } from "./dialog.service";
import { MfaService } from "./mfa.service";
import {
  WebAuthnBrowserError,
  WebAuthnBrowserService,
} from "./webauthn-browser.service";

/**
 * MFA Step-Up Service (feature F6)
 *
 * Drives the admin "step-up" re-verification flow. The backend gates every
 * `/api/v1/admin/*` request behind `admin_mfa_layer`: until the session has
 * proven a fresh MFA challenge, those requests return 403. To satisfy the gate
 * we prefer an independently enabled WebAuthn security key and otherwise POST
 * a TOTP / backup code to `POST /api/v1/auth/mfa/step-up`. On success the
 * backend sets an httpOnly `mfa_step_up` cookie (the proof). That cookie is sent
 * automatically with `withCredentials: true` on every subsequent admin request.
 * F-FU-42: the server accepts it for ordinary admin reads (GET/HEAD) while the
 * verification is at most 30 minutes old, but for mutations, secret-bearing
 * reads, and bulk exports only while it is at most five minutes old; the proof
 * survives an access-token refresh of the same session and never outlives that
 * session. Removing an MFA factor revokes it.
 *
 * NO CLIENT-SIDE EXPIRY: the step-up response's `expires_in` is the READ window
 * and is deliberately ignored here. The server is the only authority on
 * freshness — a mutation answered with `MFA_STEP_UP_REQUIRED` always opens the
 * ceremony (subject to the single-flight/cancel rules below), even when a proof
 * was established moments ago, and the interceptor replays it exactly once.
 *
 * SECURITY: the proof is an httpOnly cookie — JavaScript cannot (and must not)
 * read it. There is nothing to store in localStorage/memory and nothing to
 * attach as a header on the retried request. The backend clears the cookie on
 * logout, so there is nothing to clear here either.
 *
 * SINGLE-FLIGHT: when several concurrent admin requests 403 at once, we must
 * NOT prompt the operator N times. {@link promptAndStepUp} holds ONE shared
 * in-flight observable (mirrors AuthService.refreshTokenSingleFlight) so all
 * concurrent callers collapse to a SINGLE prompt + SINGLE step-up call. The
 * handle is cleared on settle. Failed or cancelled ceremonies require explicit
 * retry or navigation before an automatic challenge can open again.
 */
@Injectable({
  providedIn: "root",
})
export class MfaStepUpService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);
  private readonly dialogService = inject(DynamicDialogService);
  private readonly mfaService = inject(MfaService);
  private readonly generatedMfaService = inject(GeneratedMfaService);
  private readonly webAuthn = inject(WebAuthnBrowserService);

  /**
   * Shared in-flight step-up cycle (status → WebAuthn or code verification).
   * Concurrent 403s subscribe to this SAME observable instead of each opening
   * a browser prompt/dialog. Reset to null on settle (success OR error) so the
   * next explicit retry can start clean; background polls remain suppressed.
   */
  private stepUpInFlight$: Observable<void> | null = null;
  private readonly cancelForNavigation$ = new Subject<void>();
  private automaticPromptSuppressed = false;
  private navigationRecovery: { id: number; required: boolean; suppressed: boolean } | null = null;
  private _navigationId = 0;
  private _proofVersion = 0;
  get proofVersion(): number { return this._proofVersion; }
  get navigationId(): number { return this._navigationId; }
  readonly verificationRequired = signal(false);
  readonly pending = signal(false);

  constructor() {
    inject(Router).events.pipe(takeUntilDestroyed(inject(DestroyRef))).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.navigationRecovery = {
          id: event.id,
          required: this.verificationRequired(),
          suppressed: this.automaticPromptSuppressed || this.verificationRequired(),
        };
        this._navigationId += 1;
        this.cancelForNavigation$.next();
        this.automaticPromptSuppressed = false;
        this.verificationRequired.set(false);
      } else if (event instanceof NavigationEnd) {
        this.navigationRecovery = null;
      } else if ((event instanceof NavigationCancel || event instanceof NavigationError)
        && this.navigationRecovery?.id === event.id) {
        // A rejected guard or failed lazy chunk leaves the original page mounted.
        // Restore its recovery state and reject requests from the abandoned route.
        const recovery = this.navigationRecovery;
        this._navigationId += 1;
        this.cancelForNavigation$.next();
        this.automaticPromptSuppressed = recovery.suppressed;
        this.verificationRequired.set(recovery.required);
        this.navigationRecovery = null;
      }
    });
  }

  /**
   * Low-level step-up call. POSTs the supplied code to the backend step-up
   * endpoint with credentials so the resulting httpOnly cookie is accepted and
   * stored by the browser. Maps a successful response to void; lets errors
   * (notably 401 on a bad code) propagate to the caller.
   *
   * The URL is derived from `environment.apiUrl` (the same `/api/v1` base every
   * other service/the auth interceptor uses) — never hardcode the host.
   */
  stepUp(code: string): Observable<void> {
    const url = `${environment.apiUrl}/auth/mfa/step-up`;
    return this.http
      .post<{ expires_in: number }>(url, { code }, { withCredentials: true })
      .pipe(map(() => undefined));
  }

  /**
   * Single-flight factor selection + step-up.
   *
   * Loads the server's canonical independent-factor state once. A usable
   * security key is preferred and completes the existing server-held WebAuthn
   * options/verify ceremony. When there is no usable key, an independently
   * enabled authenticator app or recovery code retains the existing code-dialog
   * fallback. Concurrent callers share the one in-flight result.
   *
   * - User cancels the prompt (returns null) → errors with a typed
   *   'MFA_STEP_UP_CANCELLED' so the interceptor can propagate the ORIGINAL 403
   *   without retrying.
   * - Invalid code format → errors 'MFA_STEP_UP_INVALID_CODE' (no network call).
   * - Step-up POST fails (e.g. 401 wrong code) → that error propagates.
   *
   * On success emits void exactly once; the httpOnly cookie is now set and the
   * caller may retry the original admin request.
   */
  promptAndStepUp(explicitRetry = false, navigationId = this.navigationId, proofVersion = this.proofVersion, context?: string): Observable<void> {
    // RF-QA-196: `context` (e.g. "Symbol mapping data") lets the dialog say
    // WHAT is being protected. Optional and appended last so every existing
    // caller keeps its signature; when several concurrent 403s share one
    // in-flight cycle the first caller's context wins.
    if (navigationId !== this.navigationId) {
      return throwError(() => new Error('MFA_STEP_UP_CANCELLED'));
    }
    // A slow response from a request sent before the last successful ceremony
    // can arrive after the shared cycle settles. Retry with the new cookie;
    // the interceptor still permits exactly one replay and the server decides.
    if (!explicitRetry && proofVersion < this.proofVersion) return of(undefined);
    if (this.stepUpInFlight$) return this.stepUpInFlight$;
    if (this.automaticPromptSuppressed && !explicitRetry) {
      return throwError(() => new Error('MFA_STEP_UP_CANCELLED'));
    }

    this.verificationRequired.set(true);
    this.pending.set(true);
    const cycle$ = race(
      // RF-QA-171: shared single-flight status (the route guard reads the same
      // endpoint at load) instead of a private generated-client call.
      defer(() => this.mfaService.fetchStatus()).pipe(
        take(1),
        switchMap(status => this.runPreferredStepUp(status, context)),
      ),
      this.cancelForNavigation$.pipe(
        take(1),
        switchMap(() => throwError(() => new Error('MFA_STEP_UP_CANCELLED'))),
      ),
    ).pipe(
      tap(() => {
        this._proofVersion += 1;
        this.verificationRequired.set(false);
        this.automaticPromptSuppressed = false;
      }),
      catchError((error: unknown) => {
        // Any failed ceremony needs a deliberate retry. Polling must never
        // restart an operator-cancelled prompt or a failed verification loop.
        if (navigationId === this.navigationId) this.automaticPromptSuppressed = true;
        return throwError(() => error);
      }),
      finalize(() => {
        this.stepUpInFlight$ = null;
        this.pending.set(false);
      }),
      // Cache errors too: a late subscriber to this cycle must not reopen it.
      share({ connector: () => new ReplaySubject<void>(1), resetOnError: false,
        resetOnComplete: false, resetOnRefCountZero: false }),
    );
    this.stepUpInFlight$ = cycle$;
    return cycle$;
  }

  private runPreferredStepUp(status: MfaStatusResponse, context?: string): Observable<void> {
    const advertisedMethods = new Set(status.methods ?? []);
    const hasSecurityKey =
      status.securityKeys.available &&
      status.securityKeys.enabled &&
      status.securityKeys.count > 0 &&
      advertisedMethods.has("security_key");
    const canUseCode =
      (status.authenticatorApp.enabled && advertisedMethods.has("totp")) ||
      (status.backupCodesRemaining > 0 && advertisedMethods.has("backup_code"));

    if (hasSecurityKey && this.webAuthn.isSupported()) {
      return this.stepUpWithSecurityKey(canUseCode, context);
    }

    if (canUseCode) {
      return this.promptForCode(context);
    }

    const reason = hasSecurityKey
      ? "MFA_STEP_UP_SECURITY_KEY_UNAVAILABLE"
      : "MFA_STEP_UP_NO_USABLE_METHOD";
    this.logger.warn("[MfaStepUpService] No usable admin step-up method", {
      reason,
    });
    return throwError(() => new Error(reason));
  }

  private stepUpWithSecurityKey(canUseCodeFallback: boolean, context?: string): Observable<void> {
    return this.generatedMfaService.beginStepUp().pipe(
      take(1),
      catchError((error: unknown) => {
        // The options contract uses 400 specifically for "no security key
        // enrolled". This closes the status/options race where another tab
        // removes the last key after status was read. Do not mask auth, CSRF,
        // rate-limit, network, configuration, or server failures.
        if (
          canUseCodeFallback &&
          error instanceof HttpErrorResponse &&
          error.status === 400
        ) {
          this.logger.warn(
            "[MfaStepUpService] Security key became unavailable; falling back to an enabled code method",
          );
          return of(null);
        }
        return throwError(() => error);
      }),
      switchMap((challenge) => {
        if (challenge === null) {
          return this.promptForCode(context);
        }

        const abortController = new AbortController();

        return defer(() =>
          from(
            this.webAuthn.authenticate(
              challenge.publicKey,
              abortController.signal,
            ),
          ),
        ).pipe(
          switchMap((credential) =>
            this.generatedMfaService.finishStepUp({
              ceremonyId: challenge.ceremonyId,
              credential,
            }),
          ),
          map(() => undefined),
          catchError((error: unknown) => {
            if (!(error instanceof WebAuthnBrowserError)) {
              return throwError(() => error);
            }

            if (error.code === "aborted" || error.code === "not-allowed") {
              this.logger.debug(
                "[MfaStepUpService] Security-key prompt cancelled by user",
              );
              return throwError(() => new Error("MFA_STEP_UP_CANCELLED"));
            }

            if (canUseCodeFallback) {
              this.logger.warn(
                "[MfaStepUpService] Security-key browser flow failed; falling back to an enabled code method",
                { code: error.code },
              );
              return this.promptForCode(context);
            }

            return throwError(() => error);
          }),
          finalize(() => {
            if (!abortController.signal.aborted) {
              abortController.abort();
            }
          }),
        );
      }),
    );
  }

  private promptForCode(context?: string): Observable<void> {
    // Login-styled Two-Factor Authentication card (see
    // MfaStepUpDialogComponent), matching the inline MFA login challenge.
    // RF-QA-196: `context` is only added when supplied so the dialog data (and
    // the existing exact-argument assertions) are unchanged for callers without one.
    return this.dialogService
      .promptMfaCode({
        title: "Admin security verification",
        message:
          "Admin access requires a separate recent verification, even after MFA login. Viewing admin data stays verified for up to 30 minutes; making changes, viewing secrets, or exporting data needs a verification from the last 5 minutes. Enter an authenticator or backup code. Cancelling leaves this request unverified.",
        ...(context ? { context } : {}),
      })
      .pipe(
        take(1),
        switchMap((code: string | null) => {
          if (code == null) {
            this.logger.debug(
              "[MfaStepUpService] Step-up prompt cancelled by user",
            );
            return throwError(() => new Error("MFA_STEP_UP_CANCELLED"));
          }

          const trimmed = code.trim();
          const valid =
            this.mfaService.isValidTotpCode(trimmed) ||
            this.mfaService.isValidBackupCode(trimmed);
          if (!valid) {
            this.logger.warn(
              "[MfaStepUpService] Step-up code failed format validation",
            );
            return throwError(() => new Error("MFA_STEP_UP_INVALID_CODE"));
          }

          return this.stepUp(trimmed);
        }),
      );
  }
}
